/* import __COLOCATED_TEMPLATE__ from './fly-color-picker.hbs.js'; */
var _class, _descriptor;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { colord } from 'colord';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { next, throttle } from '@ember/runloop';
import { WHITE } from '@customerio/fly-components/utils/colors';
const DEFAULT_COLOR = WHITE;
const TRANSPARENCY_SVG = `
<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="8" height="8" fill="#F7F7F7"/>
<path fill="#BFBFBF" d="M0 0h4v8h4v-4h-8z"/>
</svg>
`;
const TRANSPARENCY_IMAGE = `data:image/svg+xml;base64,${window.btoa(TRANSPARENCY_SVG.trim())}`;
const SV_CURSOR_OFFSET = 6;
const HUE_CURSOR_OFFSET = 2;

/**
  FlyColorPicker is a semi-full-featured color picker. It allows for a custom,
  non-transparent color to be selected and turned into a HEX color.

  ```hbs
  <FlyColorPicker
    /@color={{this.color}}
    /@disabled={{this.disabled}}
    /@allowEmpty={{this.allowEmpty}}
    /@onColorChange={{this.onColorChange}}
  />
  ```

  @class FlyColorPicker
  @public
 */
let FlyColorPicker = (_class = class FlyColorPicker extends Component {
  get color() {
    if (this.args.color == null || this.args.color === '') {
      return DEFAULT_COLOR;
    }
    return this.args.color;
  }
  constructor(owner, args) {
    super(owner, args);
    _defineProperty(this, "sVInteractiveElement", null);
    _defineProperty(this, "hueInteractiveElement", null);
    _defineProperty(this, "sVContext", null);
    // When the user starts picking a color and moves their mouse around with the
    // button pressed, we allow them to exceed the bounds of the dropdown to
    // select a color until they release their mouse button. We need to track if
    // they're dragging while selecting a color in order to prevent the dropdown
    // from closing when they release the button
    _defineProperty(this, "isDragging", false);
    // HSV has more fidelity than HEX values, so we need to store an internal
    // representation to not lose any detail.
    // An example is dragging the picker to the bottom. Brightness will be 0,
    // but saturation could technically be 0% to 100%. In hex, that's always
    // #000000, which is represented as single point on the bottom row
    _initializerDefineProperty(this, "hsv", _descriptor, this);
    this.hsv = colord(this.color).toHsv();
    registerDestructor(this, () => {
      this.cleanupSVHandlers();
      this.cleanupHueHandlers();
    });
  }
  get triggerPreviewStyles() {
    if (this.args.color == null || this.args.color === '') {
      return `background: url(${TRANSPARENCY_IMAGE}); background-repeat: repeat`;
    }
    return `background: ${this.args.color}`;
  }
  get sVCursorStyles() {
    return [`top: ${100 - this.hsv.v}%`, `left: ${this.hsv.s}%`, `background-color: ${this.color}`, `transform: translate(-${SV_CURSOR_OFFSET}px, -${SV_CURSOR_OFFSET}px)`].join(';');
  }
  get hueCursorStyles() {
    return [`left: ${this.hsv.h / 360 * 100}%`, `background-color: hsl(${this.hsv.h}, 100%, 50%)`, `transform: translateX(-${HUE_CURSOR_OFFSET}px)`].join(';');
  }

  // The canvas represents the saturation and value parts of the HSV color model
  //
  // 0% Saturation -----> 100%
  // |                    Brightness
  // |                    |
  // |                    |
  // |                    |
  // |                    |
  // |                    |
  // |                    V
  // |___________________ 0%
  renderSVCanvas(el) {
    let ctx = el.getContext('2d', {
      alpha: false
    });
    if (ctx == null) {
      return;
    }
    this.sVContext = ctx;
    this.drawSVCanvas();
  }
  drawSVCanvas() {
    if (this.sVInteractiveElement == null || this.sVContext == null) {
      return;
    }
    let {
      clientWidth: width
    } = this.sVInteractiveElement;
    let horizontalGradient = this.sVContext.createLinearGradient(0, 0, width, 0);
    horizontalGradient.addColorStop(0, WHITE);
    horizontalGradient.addColorStop(1, `hsl(${this.hsv.h}, 100%, 50%)`);
    this.sVContext.fillStyle = horizontalGradient;
    this.sVContext.fillRect(0, 0, width, width);
    let verticalGradient = this.sVContext.createLinearGradient(0, 0, 0, width);
    verticalGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    verticalGradient.addColorStop(1, 'rgba(0, 0, 0, 1.0)');
    this.sVContext.fillStyle = verticalGradient;
    this.sVContext.fillRect(0, 0, width, width);
  }
  renderHueCanvas(el) {
    let ctx = el.getContext('2d', {
      alpha: false
    });
    if (ctx == null) {
      return;
    }
    let {
      clientWidth: width
    } = el;
    let horizontalGradient = ctx.createLinearGradient(0, 0, width, 0);
    let stops = 14;
    horizontalGradient.addColorStop(0, 'hsl(0, 100%, 50%)');

    // fill all the colors of the rainbow
    for (let i = 1; i < stops; i++) {
      horizontalGradient.addColorStop(i / stops, `hsl(${i / stops * 360}, 100%, 50%)`);
    }
    ctx.fillStyle = horizontalGradient;
    ctx.fillRect(0, 0, width, width);
  }
  storeSVInteractiveElement(el) {
    this.sVInteractiveElement = el;
    this.drawSVCanvas();
  }
  storeHueInteractiveElement(el) {
    this.hueInteractiveElement = el;
  }
  handleSVMouseDown(evt) {
    if (this.sVInteractiveElement == null || this.args.disabled) {
      return;
    }

    // Mouse move without clicking on the button
    if (evt.buttons !== 1) {
      return;
    }
    let {
      x: mouseX,
      y: mouseY
    } = evt;
    let {
      x: containerX,
      y: containerY,
      width
    } = this.sVInteractiveElement.getBoundingClientRect();
    let [x, y] = [mouseX - containerX, mouseY - containerY];
    //. clamp the X and Y to the frame of the interactive element
    let boundedX = Math.max(0, Math.min(width, x));
    let boundedY = Math.max(0, Math.min(width, y));
    let saturation = boundedX / width * 100;
    let value = 100 - boundedY / width * 100; // value is 1 at the top and 0 at the bottom, so we need to flip this
    let newHSV = {
      h: this.hsv.h,
      s: saturation,
      v: value,
      a: 1
    };
    let newHex = colord(newHSV).toHex();
    this.hsv = newHSV;
    this.args.onColorChange(newHex);
    this.isDragging = true;
    // `color-picker-select-none` is added to the body to prevent text on the
    // page from being highlighted if the user moves their mouse outside the
    // interactive element while still holding down a mouse button (which is valid)
    document.body.classList.add('color-picker-select-none');
    window.addEventListener('mousemove', this.handleSVMouseDown);
    window.addEventListener('mouseup', this.handleSVMouseUp);
  }
  handleSVMouseUp(evt) {
    if (this.args.disabled) {
      return;
    }
    evt.stopPropagation();
    this.cleanupSVHandlers();

    // Set this on the next tick, because then onClose handler for
    // ember-basic-dropdown will run after this function, and we still need to
    // know if it was being dragged outside of the dropdown content.
    next(() => {
      if (!this.isDestroyed && !this.isDestroying) {
        this.isDragging = false;
      }
    });
  }
  cleanupSVHandlers() {
    document.body.classList.remove('color-picker-select-none');
    window.removeEventListener('mouseup', this.handleSVMouseUp);
    window.removeEventListener('mousemove', this.handleSVMouseDown);
  }
  handleHueMouseDown(evt) {
    if (this.hueInteractiveElement == null || this.args.disabled) {
      return;
    }

    // Mouse move without clicking on the button
    if (evt.buttons !== 1) {
      return;
    }
    let {
      x: mouseX
    } = evt;
    let {
      x: containerX,
      width
    } = this.hueInteractiveElement.getBoundingClientRect();
    let x = mouseX - containerX;
    let boundedX = Math.max(0, Math.min(width, x));
    let hue = boundedX / width * 360;
    let newHSV = {
      h: hue,
      s: this.hsv.s,
      v: this.hsv.v,
      a: 1
    };
    let newHex = colord(newHSV).toHex();
    this.hsv = newHSV;
    this.drawSVCanvas();
    this.args.onColorChange(newHex);
    this.isDragging = true;
    // `color-picker-select-none` is added to the body to prevent text on the
    // page from being highlighted if the user moves their mouse outside the
    // interactive element while still holding down a mouse button (which is valid)
    document.body.classList.add('color-picker-select-none');
    window.addEventListener('mousemove', this.handleHueMouseDown);
    window.addEventListener('mouseup', this.handleHueMouseUp);
  }
  handleHueMouseUp(evt) {
    if (this.args.disabled) {
      return;
    }
    evt.stopPropagation();
    this.cleanupHueHandlers();

    // Set this on the next tick, because then onClose handler for
    // ember-basic-dropdown will run after this function, and we still need to
    // know if it was being dragged outside of the dropdown content.
    next(() => {
      if (!this.isDestroyed && !this.isDestroying) {
        this.isDragging = false;
      }
    });
  }
  cleanupHueHandlers() {
    document.body.classList.remove('color-picker-select-none');
    window.removeEventListener('mouseup', this.handleHueMouseUp);
    window.removeEventListener('mousemove', this.handleHueMouseDown);
  }
  handleDropdownClose() {
    return !this.isDragging;
  }
  handleWheel(evt) {
    if (this.args.disabled) {
      return;
    }
    let {
      deltaY,
      deltaX
    } = evt;
    let [absY, absX] = [Math.abs(deltaY), Math.abs(deltaX)];
    let delta = absY > absX ? deltaY : deltaX;
    // reduce delta impact by a percentage so that the hue doesn't change too fast
    let limitFactor = 0.4;
    let newHue = Math.max(0, Math.min(360, this.hsv.h + delta * limitFactor));
    this.hsv = {
      ...this.hsv,
      h: newHue
    };
    this.args.onColorChange(colord(this.hsv).toHex());
    this.drawSVCanvas();
  }
  throttledHandleWheel(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    return throttle(this, this.handleWheel, evt, 50, true);
  }
  updateColor(evt) {
    let rawValue = evt.target.value;
    let inputColor = colord(rawValue);
    if (!inputColor.isValid()) {
      // probably not the best error handling, but I'm not sure it's worth an
      // error message?
      evt.target.value = this.args.color;
      return;
    }
    this.args.onColorChange(inputColor.toHex());
    this.hsv = inputColor.toHsv();
    this.drawSVCanvas();
  }
  clearColor(dd) {
    this.args.onColorChange(null);
    dd.actions.close();
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "hsv", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "renderSVCanvas", [action], Object.getOwnPropertyDescriptor(_class.prototype, "renderSVCanvas"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "drawSVCanvas", [action], Object.getOwnPropertyDescriptor(_class.prototype, "drawSVCanvas"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "renderHueCanvas", [action], Object.getOwnPropertyDescriptor(_class.prototype, "renderHueCanvas"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "storeSVInteractiveElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "storeSVInteractiveElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "storeHueInteractiveElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "storeHueInteractiveElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleSVMouseDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleSVMouseDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleSVMouseUp", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleSVMouseUp"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cleanupSVHandlers", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cleanupSVHandlers"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleHueMouseDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleHueMouseDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleHueMouseUp", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleHueMouseUp"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cleanupHueHandlers", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cleanupHueHandlers"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleDropdownClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleDropdownClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleWheel", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleWheel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "throttledHandleWheel", [action], Object.getOwnPropertyDescriptor(_class.prototype, "throttledHandleWheel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateColor", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateColor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearColor", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearColor"), _class.prototype)), _class);
export { FlyColorPicker as default };